import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOnboarding, useAppStore } from '@/store';
import { useCreateProfile } from '@/hooks/api';
import { Button, Input, Textarea, Badge, Card } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { TECH_STACK_OPTIONS, LIFESTYLE_OPTIONS } from '@/types';
import { cn, processImageFile } from '@/utils';
import { api } from '@/api';

// Validation schemas for each step
const stepSchemas = {
  0: z.object({
    photos: z.array(z.any()).min(1, 'Добавьте хотя бы одно фото'),
  }),
  1: z.object({
    name: z.string().min(2, 'Имя должно быть минимум 2 символа'),
    birthdate: z.string().min(1, 'Выберите дату рождения').refine((date) => {
      const birthDate = new Date(date);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const isOver18 = age > 18 || (age === 18 && monthDiff >= 0);
      return isOver18;
    }, 'Вам должно быть минимум 18 лет'),
    bio: z.string().min(20, 'Опишите себя подробнее (минимум 20 символов)').max(500, 'Слишком длинное описание (максимум 500 символов)'),
    city: z.string().optional(),
    country: z.string().optional(),
  }),
  2: z.object({
    specializationId: z.number().min(1, 'Выберите специализацию'),
    techStackIds: z.array(z.number()).optional(),
  }),
  3: z.object({
    desiredLifestyleId: z.number().min(1, 'Выберите искомый стиль жизни'),
    hobbyIds: z.array(z.number()).optional(),
  }),
};

const OnboardingScreen: React.FC = () => {
  const { currentStep, data, setOnboardingStep, updateOnboardingData } = useOnboarding();
  const { setActiveScreen, user } = useAppStore();
  const createProfile = useCreateProfile();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState<string[]>([]);

  const isMale = data.gender === 'male';
  const isFemale = data.gender === 'female';
  // For males, is_it is always true. For females, check user.is_it
  const isIT = isMale ? true : (user?.is_it === true);

  // Check if photos are being uploaded (for step 0)
  const [photosFullyUploaded, setPhotosFullyUploaded] = useState(false);

  const currentSchema = stepSchemas[currentStep as keyof typeof stepSchemas];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm({
    resolver: zodResolver(currentSchema),
    defaultValues: data,
    mode: 'onChange',
  });

  // Clear uploadedPhotoUrls when gender changes (new registration starts)
  useEffect(() => {
    console.log('🔄 Gender changed or component mounted, clearing photo URLs');
    setUploadedPhotoUrls([]);
    setPhotosFullyUploaded(false);
  }, [data.gender]);

  // Reset form when step changes to use correct schema and default values
  useEffect(() => {
    console.log('🔄 Step changed to:', currentStep, 'Resetting form with current schema');
    // Only reset with data from onboarding store, don't trigger validation errors
    reset(data, { keepErrors: false, keepDirty: false, keepValues: false });
  }, [currentStep]);

  const watchedValues = watch();

  // Pass photo upload status to child components
  useEffect(() => {
    if (currentStep === 0) {
      // Will be set by PhotoStep component
    }
  }, [currentStep]);

  // Configure Telegram buttons
  useEffect(() => {
    const isLastStep = currentStep === 4;
    const canGoBack = currentStep > 0;

    // Configure back button
    TelegramUtils.configureBackButton(canGoBack, () => {
      if (currentStep > 0) {
        setOnboardingStep(currentStep - 1);
      }
    });

    // Configure main button
    TelegramUtils.configureMainButton({
      visible: true,
      text: isLastStep ? 'Complete Profile' : 'Continue',
      active: isValid,
      progress: createProfile.isPending,
      onClick: () => {
        handleSubmit(onSubmit)();
      },
    });

    return () => {
      TelegramUtils.configureBackButton(false);
      TelegramUtils.configureMainButton({ visible: false });
    };
  }, [currentStep, isValid, createProfile.isPending, handleSubmit]);

  const onSubmit = async (formData: any) => {
    // Prevent multiple submissions
    if (isSaving) {
      console.log('⚠️ Already saving, ignoring duplicate submission');
      return;
    }

    // Save current step data locally with proper type conversion
    const cleanedData = {
      ...formData,
      // Ensure numeric fields are numbers
      specializationId: formData.specializationId ? Number(formData.specializationId) : undefined,
      desiredLifestyleId: formData.desiredLifestyleId ? Number(formData.desiredLifestyleId) : undefined,
      techStackIds: formData.techStackIds?.map((id: any) => Number(id)) || [],
      hobbyIds: formData.hobbyIds?.map((id: any) => Number(id)) || [],
    };

    console.log('💾 Saving step data:', {
      step: currentStep,
      cleanedData,
    });

    updateOnboardingData(cleanedData);
    setIsSaving(true);

    try {
      // Step 0: Photos - photos are already uploaded individually via api.uploadPhoto()
      if (currentStep === 0) {
        console.log('📝 Photos step completed');
        console.log('📊 Photos in form:', formData.photos?.length);

        // Photos are uploaded individually through api.uploadPhoto() in PhotoStep
        // Zod validation already ensures we have at least 1 photo
        TelegramUtils.haptic.success();
      }

      // Step 1: Basic Info - call PUT /profile/basic
      if (currentStep === 1) {
        console.log('📝 Saving basic info to API...');

        await (api as any).updateBasicInfo({
          first_name: formData.name,
          birthdate: formData.birthdate,
          bio: formData.bio,
          city: formData.city,
          country: formData.country,
        });

        console.log('✅ Basic info saved to server');
        TelegramUtils.haptic.success();
      }

      // Step 2: Tech Stack (IT users only) - call PUT /profile/it-info
      if (currentStep === 2 && isIT) {
        console.log('📝 Saving tech stack to API...');
        console.log('📊 Form data:', formData);
        console.log('📊 Stored onboarding data:', data);

        // Get specializationId from formData OR from stored onboarding data
        const specializationId = formData.specializationId || (data as any).specializationId;

        console.log('🔍 Using specializationId:', specializationId);

        if (specializationId) {
          await (api as any).updateITInfo({
            specializations: [{
              specialization_id: Number(specializationId),
            }],
            tech_stack_ids: formData.techStackIds || [],
          });
          console.log('✅ Tech stack saved to server');
          TelegramUtils.haptic.success();
        } else {
          // No specialization selected - just skip, backend will handle validation if needed
          console.warn('⚠️ specializationId is missing, skipping IT info save');
        }
      }

      // Step 3: Lifestyle - call PUT /profile/lifestyle + PUT /profile/it-info (for hobbies)
      if (currentStep === 3) {
        console.log('📝 Saving lifestyle to API...');

        // Save lifestyle tag (required)
        if (formData.desiredLifestyleId) {
          await (api as any).updateLifestyle(formData.desiredLifestyleId);
          console.log('✅ Lifestyle tag saved to server');
        } else {
          throw new Error('Lifestyle tag is required');
        }

        // Save hobbies (optional) - only call API if there are hobbies AND user is IT
        if (isIT && formData.hobbyIds && formData.hobbyIds.length > 0) {
          try {
            // Get specializationId from stored onboarding data (saved on step 2)
            const specializationId = (data as any).specializationId;
            const techStackIds = (data as any).techStackIds || [];

            console.log('📝 Saving hobbies with specialization info:', {
              specializationId,
              techStackIds,
              hobbyIds: formData.hobbyIds
            });

            // Include specialization info to pass backend validation
            await (api as any).updateITInfo({
              specializations: specializationId ? [{
                specialization_id: Number(specializationId),
              }] : undefined,
              tech_stack_ids: techStackIds,
              hobby_ids: formData.hobbyIds,
            });
            console.log('✅ Hobbies saved to server');
          } catch (hobbyError: any) {
            // Log but don't fail - hobbies are optional
            console.warn('⚠️ Failed to save hobbies (non-critical):', hobbyError);
          }
        }

        TelegramUtils.haptic.success();
        console.log('🎉 All data saved successfully!');
      }

      // Check if this is the last step
      const isLastStep = currentStep === 3;

      if (!isLastStep) {
        // Move to next step only after successful save
        let nextStep = currentStep + 1;

        // Skip tech stack for non-IT users (jump from step 1 to 3)
        if (!isIT && currentStep === 1) {
          nextStep = 3;
        }

        setOnboardingStep(nextStep);
      } else {
        // Last step completed (Lifestyle) - go to looking-for screen
        console.log('🎉 Onboarding steps completed, going to looking-for screen');
        setActiveScreen('looking-for');
      }
    } catch (error: any) {
      console.error('❌ API call failed:', error);
      TelegramUtils.haptic.error();
      await TelegramUtils.showAlert(
        `Не удалось сохранить данные: ${error.message || 'Проверьте подключение к серверу'}`
      );
      // Don't proceed on error
    } finally {
      setIsSaving(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <PhotoStep register={register} errors={errors} setValue={setValue} watch={watch} onUploadComplete={setPhotosFullyUploaded} setUploadedUrls={setUploadedPhotoUrls} isFemale={isFemale} />;
      case 1:
        return <BasicInfoStep register={register} errors={errors} isFemale={isFemale} />;
      case 2:
        return <TechStackStep register={register} errors={errors} setValue={setValue} watch={watch} isFemale={isFemale} data={data} />;
      case 3:
        return <LifestyleStep register={register} errors={errors} setValue={setValue} watch={watch} data={data} />;
      default:
        return <PhotoStep register={register} errors={errors} setValue={setValue} watch={watch} onUploadComplete={setPhotosFullyUploaded} setUploadedUrls={setUploadedPhotoUrls} isFemale={isFemale} />;
    }
  };

  // Calculate progress based on IT status
  const getStepInfo = () => {
    if (!isIT) {
      // Non-IT users: steps 0(photo), 1(info), 3(lifestyle) - skip step 2
      const nonITSteps = [0, 1, 3];
      const nonITStepIndex = nonITSteps.indexOf(currentStep);
      return {
        current: nonITStepIndex >= 0 ? nonITStepIndex + 1 : 1,
        total: 3
      };
    } else {
      // IT users: steps 0(photo), 1(info), 2(tech), 3(lifestyle)
      return { current: currentStep + 1, total: 4 };
    }
  };

  const stepInfo = getStepInfo();

  return (
    <div className={`h-screen flex flex-col bg-tg-bg safe-area-y`}>
      {/* Progress Bar */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-3">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={() => {
                  // Handle back for non-IT users (skip tech stack)
                  if (!isIT && currentStep === 3) {
                    setOnboardingStep(1); // Go back to info from lifestyle
                  } else {
                    setOnboardingStep(currentStep - 1);
                  }
                }}
                className="text-white hover:text-neutral-300 transition-colors font-medium"
              >
                ← Назад
              </button>
            )}
          </div>
          <span className="text-sm text-neutral-500 font-medium">
            Шаг {stepInfo.current} / {stepInfo.total}
          </span>
        </div>
        <div className="w-full bg-neutral-900 rounded-full h-1.5 overflow-hidden">
          <div
            className="bg-white h-1.5 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(stepInfo.current / stepInfo.total) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content - Scrollable */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col min-h-0">
        <div className={`flex-1 overflow-y-auto px-6 ${!TelegramUtils.isInTelegram ? 'pb-28' : 'pb-6'}`}>
          {renderStep()}
        </div>

        {/* Bottom Button - only show when NOT in Telegram */}
        {!TelegramUtils.isInTelegram && (
          <div className="flex-shrink-0 p-6 bg-tg-bg/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
            <Button
              type="submit"
              className="w-full h-14 bg-white text-black hover:bg-neutral-100 font-bold text-base rounded-2xl transition-all active:scale-95 disabled:opacity-50"
              disabled={!isValid || (currentStep === 0 && !photosFullyUploaded) || isSaving || createProfile.isPending}
              loading={isSaving || createProfile.isPending}
            >
              {isSaving
                ? 'Сохраняем...'
                : 'Продолжить'
              }
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

const WelcomeStep: React.FC<{ isMale?: boolean; isFemale?: boolean }> = ({ isMale, isFemale }) => (
  <div className="text-center py-8 px-2">
    {/* Logo */}
    <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-3xl mb-8 shadow-2xl">
      <span className="text-4xl font-bold text-black">IT</span>
    </div>

    {/* Заголовок */}
    <h1 className="text-4xl font-bold text-white mb-4 tracking-tight leading-tight">
      ITinder
    </h1>

    {/* Основной слоган - разный для мужчин и женщин */}
    <p className="text-xl text-neutral-300 mb-2 font-medium">
      Зима близко
    </p>
    <p className="text-lg text-neutral-500 mb-12 font-light leading-relaxed">
      {isFemale ? (
        <>Найди своего айтишника<br />для Таиланда 🏝️</>
      ) : (
        <>Найди свою пару для<br />зимовки в Таиланде 🏝️</>
      )}
    </p>

    {/* Преимущества - минималистично */}
    <div className="space-y-6 text-left max-w-sm mx-auto mb-8">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-2xl flex items-center justify-center mt-0.5">
          <span className="text-xl">✓</span>
        </div>
        <div>
          <h3 className="text-white font-semibold mb-1">
            {isFemale ? 'Верифицированные профили' : 'Подтверди свой уровень'}
          </h3>
          <p className="text-sm text-neutral-400">
            {isFemale
              ? 'Только проверенные IT-специалисты с рейтингом 0-100'
              : 'Пройди тест и получи балл 0-100 в профиле'
            }
          </p>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-2xl flex items-center justify-center mt-0.5">
          <span className="text-xl">🤖</span>
        </div>
        <div>
          <h3 className="text-white font-semibold mb-1">AI-совместимость</h3>
          <p className="text-sm text-neutral-400">Умный алгоритм подбора по tech stack и lifestyle</p>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-2xl flex items-center justify-center mt-0.5">
          <span className="text-xl">🌍</span>
        </div>
        <div>
          <h3 className="text-white font-semibold mb-1">Digital Nomad</h3>
          <p className="text-sm text-neutral-400">
            {isFemale
              ? 'Найди партнера для путешествий и зимовок'
              : 'Найди девушку для путешествий и зимовок'
            }
          </p>
        </div>
      </div>
    </div>
  </div>
);

const PhotoStep: React.FC<any> = ({ register, errors, setValue, watch, onUploadComplete, setUploadedUrls, isFemale }) => {
  const photos = watch('photos') || [];
  const [uploadingPhotos, setUploadingPhotos] = React.useState<Record<number, boolean>>({});
  const [uploadedUrlsLocal, setUploadedUrlsLocal] = React.useState<(string | undefined)[]>([]);

  // Notify parent when all photos uploaded
  React.useEffect(() => {
    const allUploaded = photos.length > 0 && photos.length === uploadedUrlsLocal.length;
    if (onUploadComplete) {
      onUploadComplete(allUploaded);
    }
    // Update parent with URLs
    if (setUploadedUrls) {
      setUploadedUrls(uploadedUrlsLocal);
    }
  }, [photos.length, uploadedUrlsLocal.length, onUploadComplete, setUploadedUrls, uploadedUrlsLocal]);

  const handlePhotoClick = async (index: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // Improve reliability in iOS/WKWebView/Telegram by attaching to DOM
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.style.top = '0';
    input.multiple = false;
    try {
      document.body.appendChild(input);
    } catch { }
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        console.log('📸 Photo selected:', { name: file.name, size: file.size, type: file.type });

        // Always use the clicked index - simple and predictable
        const targetIndex = index;

        // Start upload indicator
        setUploadingPhotos(prev => ({ ...prev, [targetIndex]: true }));
        TelegramUtils.haptic.light();

        try {
          // Ensure token is set before upload
          const savedToken = localStorage.getItem('auth_token');
          console.log('🔑 Token check:', { hasToken: !!savedToken, apiHasToken: !!(api as any).token });

          if (savedToken && !(api as any).token) {
            console.log('⚠️ Token exists but not set in API client, setting it now...');
            api.setToken(savedToken);
          }

          // Process image (convert HEIC, compress, validate)
          console.log('🔄 Processing image...');
          const processedFile = await processImageFile(file);
          console.log('✅ Image processed:', { name: processedFile.name, size: processedFile.size });

          // Show preview immediately after processing
          const newPhotos = [...photos];
          newPhotos[targetIndex] = processedFile;
          setValue('photos', newPhotos, { shouldValidate: true });

          // Upload to server
          console.log('📤 Uploading to server...');
          const result = await api.uploadPhoto(processedFile);
          console.log('✅ Upload successful:', result);

          // Save URL at the same index
          const newUrls = [...uploadedUrlsLocal];
          newUrls[targetIndex] = result.url;
          setUploadedUrlsLocal(newUrls);

          console.log('✅ Photo uploaded to slot', targetIndex, ':', result.url);
          TelegramUtils.haptic.success();
        } catch (error: any) {
          console.error('Failed to upload photo:', error);
          TelegramUtils.haptic.error();

          // Remove photo on error
          const newPhotos = [...photos];
          newPhotos[targetIndex] = undefined;
          setValue('photos', newPhotos, { shouldValidate: true });

          await TelegramUtils.showAlert(error.message || 'Не удалось загрузить фото. Попробуйте еще раз.');
        } finally {
          setUploadingPhotos(prev => ({ ...prev, [targetIndex]: false }));
          // Clean up input element to avoid stale state and allow same file re-selection
          try {
            input.value = '';
            document.body.removeChild(input);
          } catch { }
        }
      }
    };
    input.click();
  };

  const handlePhotoRemove = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();

    // Remove photo at specific index (don't splice, just set to undefined)
    const newPhotos = [...photos];
    newPhotos[index] = undefined;
    setValue('photos', newPhotos, { shouldValidate: true });

    // Remove URL at the same index
    const newUrls = [...uploadedUrlsLocal];
    newUrls[index] = undefined;
    setUploadedUrlsLocal(newUrls);

    TelegramUtils.haptic.light();
  };

  // Check if all photos uploaded (count non-empty slots)
  const photoCount = photos.filter(Boolean).length;
  const uploadedCount = uploadedUrlsLocal.filter(Boolean).length;
  const allPhotosUploaded = photoCount > 0 && photoCount === uploadedCount;
  const isUploading = Object.values(uploadingPhotos).some(Boolean);

  return (
    <div>
      <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">Добавь фото</h2>
      <p className="text-neutral-400 mb-8 leading-relaxed">
        Покажи себя {isFemale ? 'настоящую' : 'настоящего'}<br />
        <span className="text-neutral-500 text-sm">Профили с несколькими фото получают в 3 раза больше лайков</span>
      </p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handlePhotoClick(index)}
            className={cn(
              'aspect-[3/4] rounded-2xl border-2 border-dashed flex items-center justify-center relative overflow-hidden transition-all',
              {
                'border-neutral-800 bg-neutral-900/50 hover:bg-neutral-900 hover:border-neutral-700': !photos[index],
                'border-white bg-neutral-900': photos[index],
              }
            )}
          >
            {photos[index] ? (
              <>
                <img
                  src={photos[index] instanceof File ? URL.createObjectURL(photos[index]) : photos[index]}
                  alt={`Фото ${index + 1}`}
                  className="w-full h-full object-cover"
                />

                {/* Upload loader */}
                {uploadingPhotos[index] && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                {/* Remove button */}
                {!uploadingPhotos[index] && (
                  <button
                    type="button"
                    onClick={(e) => handlePhotoRemove(index, e)}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/70 rounded-full flex items-center justify-center hover:bg-black transition-colors z-10"
                  >
                    <span className="text-white text-lg">×</span>
                  </button>
                )}
              </>
            ) : (
              <div className="text-center pointer-events-none">
                <div className="w-8 h-8 bg-neutral-700 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="text-neutral-400">+</span>
                </div>
                <span className="text-xs text-neutral-500">
                  {index === 0 ? 'Главное фото' : `Фото ${index + 1}`}
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Upload status */}
      {isUploading && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-sm text-white">
            ⏳ Загружаем фото на сервер...
          </p>
        </div>
      )}

      {!allPhotosUploaded && photos.length > 0 && !isUploading && (
        <div className="bg-warning-500/10 border border-warning-500/30 rounded-xl p-4">
          <p className="text-sm text-warning-400">
            ⚠️ Дождитесь завершения загрузки фото
          </p>
        </div>
      )}

      {errors.photos && (
        <p className="text-danger-400 text-sm mt-2">{errors.photos.message}</p>
      )}
    </div>
  );
};

const BasicInfoStep: React.FC<any> = ({ register, errors, isFemale }) => {
  // Calculate max date (18 years ago)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const maxDateString = maxDate.toISOString().split('T')[0];

  // Calculate min date (65 years ago)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 65);
  const minDateString = minDate.toISOString().split('T')[0];

  return (
    <div>
      <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">О тебе</h2>
      <p className="text-neutral-400 mb-8 leading-relaxed">
        Первое впечатление — самое важное
      </p>

      <div className="space-y-6">
        <Input
          label="Имя"
          placeholder={isFemale ? "Анна" : "Александр"}
          {...register('name')}
          error={errors.name?.message}
        />

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Дата рождения
          </label>
          <input
            type="date"
            max={maxDateString}
            min={minDateString}
            {...register('birthdate')}
            className="w-full px-4 py-3 text-base bg-tg-secondary-bg border border-neutral-700 rounded-xl placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20 transition-all duration-200 min-h-[44px] text-white"
          />
          {errors.birthdate && (
            <p className="mt-2 text-sm text-danger-400">
              {errors.birthdate.message}
            </p>
          )}
        </div>

        <Textarea
          label="О себе"
          placeholder={
            isFemale
              ? "Люблю путешествия, новые впечатления и интересные проекты. Хочу найти партнера для приключений и зимовок в теплых странах..."
              : "Fullstack разработчик, люблю путешествия и кодить из Бали. Ищу партнершу для digital nomad жизни..."
          }
          {...register('bio')}
          error={errors.bio?.message}
          rows={5}
        />

        <Input
          label="Город"
          placeholder="Москва"
          {...register('city')}
          error={errors.city?.message}
        />
        <p className="text-xs text-neutral-500 -mt-4">
          Опционально • Текущий или желаемый для путешествия
        </p>

        <Input
          label="Страна"
          placeholder="Россия"
          {...register('country')}
          error={errors.country?.message}
        />
        <p className="text-xs text-neutral-500 -mt-4">
          Опционально • Текущая или желаемая для путешествия
        </p>

        <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-4">
          <p className="text-xs text-neutral-500">
            💡 Профили с подробным описанием получают на 40% больше откликов
          </p>
        </div>
      </div>
    </div>
  );
};

const TechStackStep: React.FC<any> = ({ register, errors, setValue, watch, isFemale, data }) => {
  const { setOnboardingStep, updateUser } = useAppStore();
  const [specializations, setSpecializations] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [initialized, setInitialized] = React.useState(false);
  const [switchingToNonIT, setSwitchingToNonIT] = React.useState(false);

  const selectedSpecializationId = watch('specializationId');
  const selectedTechStackIds = watch('techStackIds') || [];

  // Initialize from onboarding data if available (after specializations loaded)
  React.useEffect(() => {
    if (initialized || specializations.length === 0) return;

    console.log('🔄 TechStackStep: Initializing from data:', data);

    if (data?.specializationId && typeof data.specializationId === 'number') {
      console.log('✅ Setting specializationId:', data.specializationId);
      setValue('specializationId', data.specializationId, { shouldValidate: true });
    }
    if (data?.techStackIds && Array.isArray(data.techStackIds)) {
      console.log('✅ Setting techStackIds:', data.techStackIds);
      setValue('techStackIds', data.techStackIds.map((id: any) => Number(id)));
    }

    setInitialized(true);
  }, [specializations, data, initialized]);

  // Load specializations from API
  React.useEffect(() => {
    const loadSpecializations = async () => {
      try {
        console.log('📡 Loading specializations from API...');

        const data = await (api as any).getSpecializations(true);
        console.log('✅ Loaded specializations:', data);
        console.log('📊 Specializations count:', data?.length);

        if (data && Array.isArray(data)) {
          setSpecializations(data);
        } else {
          console.error('❌ Invalid specializations format:', data);
        }
      } catch (error: any) {
        console.error('❌ Failed to load specializations:', error);
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack,
        });

        // Show error to user
        TelegramUtils.showAlert(`Не удалось загрузить специализации: ${error.message || 'Проверьте подключение'}`);
      } finally {
        setLoading(false);
      }
    };
    loadSpecializations();
  }, []);

  const handleSpecializationSelect = (specId: number) => {
    // Ensure specId is a number, not string
    const numericId = typeof specId === 'string' ? parseInt(specId, 10) : specId;
    console.log('🎯 TechStackStep: Specialization selected:', numericId);
    setValue('specializationId', numericId, { shouldValidate: true });
    // Clear tech stack when changing specialization
    setValue('techStackIds', []);
    TelegramUtils.haptic.selection();
  };

  const toggleTech = (techId: number) => {
    const updated = selectedTechStackIds.includes(techId)
      ? selectedTechStackIds.filter((id: number) => id !== techId)
      : [...selectedTechStackIds, techId];
    console.log('🎯 TechStackStep: Tech stack updated:', updated);
    setValue('techStackIds', updated, { shouldValidate: true });
    TelegramUtils.haptic.selection();
  };

  const selectedSpecialization = specializations.find(s => s.id === selectedSpecializationId);

  const handleSwitchToNonIT = async () => {
    try {
      setSwitchingToNonIT(true);
      console.log('🔄 Switching user to non-IT...');

      await (api as any).updateBasicInfo({ is_it: false });

      console.log('✅ User switched to non-IT');
      TelegramUtils.haptic.success();

      // Update user in store
      updateUser({ is_it: false });

      // Go to step 3 (lifestyle)
      setOnboardingStep(3);
    } catch (error: any) {
      console.error('❌ Failed to switch to non-IT:', error);
      TelegramUtils.haptic.error();
      await TelegramUtils.showAlert(
        `Не удалось сохранить изменения: ${error.message || 'Проверьте подключение'}`
      );
    } finally {
      setSwitchingToNonIT(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Загружаем специализации...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">Твой Tech Stack</h2>
      <p className="text-neutral-400 mb-8 leading-relaxed">
        Расскажи в чем ты {isFemale ? 'крута' : 'крут'} 🚀
      </p>

      <div className="space-y-6">
        {/* Specialization Selection */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Специализация
          </label>
          <p className="text-xs text-neutral-500 mb-4">
            Выбери твою специализацию
          </p>
          <div className="flex flex-wrap gap-2">
            {specializations.map((spec) => (
              <button
                key={spec.id}
                type="button"
                onClick={() => handleSpecializationSelect(spec.id)}
                className={cn(
                  'px-3 py-2 rounded-full text-sm font-medium transition-all',
                  selectedSpecializationId === spec.id
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                )}
              >
                {spec.name}
              </button>
            ))}
          </div>
          {errors.specializationId && (
            <p className="text-danger-400 text-sm mt-2">{errors.specializationId.message}</p>
          )}
        </div>

        {/* Tech Stack - only show when specialization is selected */}
        {selectedSpecialization && selectedSpecialization.tech_stack.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Технологии
            </label>
            <p className="text-xs text-neutral-500 mb-4">
              Выбери твой стек • {selectedTechStackIds.length} выбрано
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedSpecialization.tech_stack.map((tech: any) => (
                <button
                  key={tech.id}
                  type="button"
                  onClick={() => toggleTech(tech.id)}
                  className={cn(
                    'px-3 py-2 rounded-full text-sm font-medium transition-all',
                    selectedTechStackIds.includes(tech.id)
                      ? 'bg-white text-black'
                      : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                  )}
                >
                  {tech.name}
                </button>
              ))}
            </div>
            {errors.techStackIds && (
              <p className="text-danger-400 text-sm mt-2">{errors.techStackIds.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Button for females to switch to non-IT */}
      {isFemale && (
        <div className="mt-8 pt-6 border-t border-white/10">
          <button
            type="button"
            onClick={handleSwitchToNonIT}
            disabled={switchingToNonIT}
            className="w-full py-3 px-4 text-sm text-neutral-400 hover:text-white transition-colors disabled:opacity-50"
          >
            {switchingToNonIT ? 'Сохранение...' : 'Я передумала, я не из IT'}
          </button>
        </div>
      )}
    </div>
  );
};

const LifestyleStep: React.FC<any> = ({ register, errors, setValue, watch, data }) => {
  const selectedHobbyIds = watch('hobbyIds') || [];
  const desiredLifestyleId = watch('desiredLifestyleId');
  const [lifestyleTags, setLifestyleTags] = React.useState<any[]>([]);
  const [hobbies, setHobbies] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Initialize from onboarding data if available
  React.useEffect(() => {
    if (data?.desiredLifestyleId && typeof data.desiredLifestyleId === 'number') {
      setValue('desiredLifestyleId', data.desiredLifestyleId, { shouldValidate: true });
    }
    if (data?.hobbyIds && Array.isArray(data.hobbyIds)) {
      setValue('hobbyIds', data.hobbyIds.map((id: any) => Number(id)));
    }
  }, []);

  // Load dictionaries from API
  React.useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const [lifestyleData, hobbiesData] = await Promise.all([
          (api as any).getLifestyleTags(),
          (api as any).getHobbies(),
        ]);

        setLifestyleTags(lifestyleData);
        setHobbies(hobbiesData);
        console.log('✅ Loaded dictionaries:', { lifestyleTags: lifestyleData.length, hobbies: hobbiesData.length });
      } catch (error) {
        console.error('❌ Failed to load dictionaries:', error);
        // Fallback to hardcoded
        setLifestyleTags([
          { id: 1, name: 'Зимовка в Азии', icon: '🏝️' },
          { id: 2, name: 'Путешествия', icon: '🌍' },
          { id: 3, name: 'Стабильность', icon: '🏙️' },
        ]);
        setHobbies([]);
      } finally {
        setLoading(false);
      }
    };

    loadDictionaries();
  }, []);

  const toggleHobby = (hobbyId: number) => {
    const updated = selectedHobbyIds.includes(hobbyId)
      ? selectedHobbyIds.filter((id: number) => id !== hobbyId)
      : [...selectedHobbyIds, hobbyId];
    setValue('hobbyIds', updated);
    TelegramUtils.haptic.selection();
  };

  const handleDesiredLifestyleSelect = (tagId: number) => {
    // Ensure tagId is a number, not string
    const numericId = typeof tagId === 'string' ? parseInt(tagId, 10) : tagId;
    setValue('desiredLifestyleId', numericId, { shouldValidate: true });
    TelegramUtils.haptic.selection();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Загружаем опции...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">Lifestyle</h2>
      <p className="text-neutral-400 mb-8 leading-relaxed">
        Какой образ жизни ты ищешь?
      </p>

      {/* Desired Lifestyle - Main Choice */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-white mb-4">
          Искомый стиль жизни
        </label>
        <div className="space-y-3">
          {lifestyleTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleDesiredLifestyleSelect(tag.id)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${desiredLifestyleId === tag.id
                  ? 'border-white bg-white/10 text-white'
                  : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40'
                }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${desiredLifestyleId === tag.id ? 'border-white bg-white' : 'border-white/40'
                  }`}>
                  {desiredLifestyleId === tag.id && (
                    <div className="w-2.5 h-2.5 bg-black rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">
                    {tag.icon} {tag.name}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
        {errors.desiredLifestyleId && (
          <p className="text-danger-400 text-sm mt-2">{errors.desiredLifestyleId.message}</p>
        )}
      </div>

      {/* Hobbies */}
      {hobbies.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Хобби
          </label>
          <p className="text-xs text-neutral-500 mb-4">
            Опционально • {selectedHobbyIds.length} выбрано (максимум 10)
          </p>
          <div className="flex flex-wrap gap-2">
            {hobbies.map((hobby) => (
              <button
                key={hobby.id}
                type="button"
                onClick={() => {
                  if (selectedHobbyIds.length < 10 || selectedHobbyIds.includes(hobby.id)) {
                    toggleHobby(hobby.id);
                  }
                }}
                className={cn(
                  'px-3 py-2 rounded-full text-sm font-medium transition-all',
                  selectedHobbyIds.includes(hobby.id)
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                )}
              >
                {hobby.name}
              </button>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default OnboardingScreen;
